import React, { useCallback, useEffect, useMemo, useState } from "react";
import swal from "sweetalert";
import { toast } from "react-toastify";
import { Box } from "@material-ui/core";

import AdminMenu from "../../../components/Admin/MenuDetalhes/AdminMenu";
import CursosSelector from "../../../components/Admin/CursosSelector";
import { producaov2 as api } from "../../../services/apis";
import { getUserNode } from "../../../services/auth_node";
import { LinkFuturoUsers } from "../../../utils/Utils";
import { useRouter } from "../../../hooks/router";

import {
  Content,
  Field,
  Notice,
  SubmitButton,
  Title,
} from "./styles";

const NAME_MAX_LENGTH = 64;
const MAX_TRIAL_DAYS = 30;
const PUBLIC_LINK_BASE = "https://voitto.com.br/inscricao/plano";

const INTERVAL_OPTIONS = [
  { label: "Mensal", value: "month" },
  { label: "Anual", value: "year" },
];

const formatPriceInput = (raw) => {
  const digits = (raw || "").replace(/\D/g, "");
  if (!digits) return "";
  const padded = digits.padStart(3, "0");
  const integer = padded.slice(0, -2).replace(/^0+(?=\d)/, "");
  const cents = padded.slice(-2);
  return `${integer},${cents}`;
};

const isPriceValid = (price) => {
  if (!/^\d+,\d{2}$/.test(price)) return false;
  const numeric = parseFloat(price.replace(",", "."));
  return numeric > 0;
};

const todayMidnight = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

const parseDateInput = (value) => {
  if (!value) return null;
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day, 0, 0, 0, 0);
};

const calcTrialDays = (targetDate) => {
  const today = todayMidnight();
  const diffMs = targetDate.getTime() - today.getTime();
  return Math.round(diffMs / (1000 * 60 * 60 * 24));
};

const minDateInput = () => {
  const d = todayMidnight();
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
};

const maxDateInput = () => {
  const d = todayMidnight();
  d.setDate(d.getDate() + MAX_TRIAL_DAYS);
  return d.toISOString().slice(0, 10);
};

const PlanosLinkFuturo = () => {
  const router = useRouter();

  const [authorized, setAuthorized] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);

  const [name, setName] = useState("");
  const [price, setPrice] = useState("");
  const [interval, setIntervalValue] = useState("month");
  const [courseId, setCourseId] = useState("");
  const [cycles, setCycles] = useState("");
  const [dataPrimeiroPagamento, setDataPrimeiroPagamento] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const user = getUserNode();
    const email = (user && user.email) || "";
    const allowed = LinkFuturoUsers.includes(email);
    setAuthorized(allowed);
    setAuthChecked(true);

    if (!allowed) {
      swal(
        "Acesso negado",
        "Você não tem permissão para acessar essa página.",
        "warning",
      ).then(() => {
        router.push("/admin");
      });
    }
  }, []);

  const handleNameChange = useCallback((e) => {
    const value = e.target.value.slice(0, NAME_MAX_LENGTH);
    setName(value);
  }, []);

  const handlePriceChange = useCallback((e) => {
    setPrice(formatPriceInput(e.target.value));
  }, []);

  const handleCyclesChange = useCallback((e) => {
    const digits = e.target.value.replace(/\D/g, "");
    setCycles(digits);
  }, []);

  const handleCursoChange = useCallback((value) => {
    setCourseId(value || "");
  }, []);

  const dateLimits = useMemo(
    () => ({ min: minDateInput(), max: maxDateInput() }),
    [],
  );

  const showLinkModal = (idPlano) => {
    const link = `${PUBLIC_LINK_BASE}/${idPlano}`;
    swal({
      title: "Link futuro gerado com sucesso!",
      text: link,
      icon: "success",
      buttons: {
        copy: { text: "Copiar link", value: "copy", closeModal: false },
        cancel: { text: "Fechar", value: null, visible: true },
      },
    }).then((value) => {
      if (value === "copy") {
        navigator.clipboard.writeText(link);
        toast.success("Link copiado para a área de transferência!");
        swal.stopLoading();
        swal.close();
      }
    });
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      swal("Campo obrigatório", "Informe o nome do plano.", "error");
      return;
    }
    if (name.length > NAME_MAX_LENGTH) {
      swal(
        "Nome muito longo",
        `O nome do plano deve ter no máximo ${NAME_MAX_LENGTH} caracteres.`,
        "error",
      );
      return;
    }
    if (!isPriceValid(price)) {
      swal(
        "Valor inválido",
        "Informe o valor no formato 00,00 (deve ser maior que zero).",
        "error",
      );
      return;
    }
    if (!courseId) {
      swal("Curso obrigatório", "Selecione um curso para o plano.", "error");
      return;
    }
    const cyclesNum = Number(cycles);
    if (!Number.isInteger(cyclesNum) || cyclesNum < 1) {
      swal(
        "Ciclos inválido",
        "Informe um número inteiro maior ou igual a 1 para os ciclos.",
        "error",
      );
      return;
    }
    if (!dataPrimeiroPagamento) {
      swal(
        "Data obrigatória",
        "Informe a data do primeiro pagamento.",
        "error",
      );
      return;
    }
    const target = parseDateInput(dataPrimeiroPagamento);
    if (!target) {
      swal("Data inválida", "Informe uma data válida.", "error");
      return;
    }
    const trialDays = calcTrialDays(target);
    if (trialDays < 1) {
      swal(
        "Data inválida",
        "A data do primeiro pagamento deve ser posterior a hoje.",
        "error",
      );
      return;
    }
    if (trialDays > MAX_TRIAL_DAYS) {
      swal(
        "Data fora do limite",
        `A data do primeiro pagamento deve estar no máximo ${MAX_TRIAL_DAYS} dias à frente de hoje.`,
        "error",
      );
      return;
    }

    const payload = {
      name: name.trim(),
      price,
      interval,
      courseId: Number(courseId),
      cycles: cyclesNum,
      trialDays,
    };

    setSubmitting(true);
    try {
      const res = await api.post("/admin/planos/assinatura", payload);
      const idPlano =
        res?.data?.response?.data?.id_plano ||
        res?.data?.data?.id_plano ||
        res?.data?.id_plano;

      if (!idPlano) {
        swal(
          "Plano criado, mas sem id_plano",
          "Não foi possível obter o id do plano para montar o link. Consulte o time de TI.",
          "warning",
        );
        return;
      }

      showLinkModal(idPlano);
    } catch (err) {
      const apiMessage =
        err?.response?.data?.response?.data?.[0]?.message ||
        err?.response?.data?.message ||
        err?.message;
      swal(
        "Erro ao gerar link futuro",
        apiMessage || "Falha inesperada. Consulte o time de TI.",
        "error",
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!authChecked) {
    return null;
  }

  if (!authorized) {
    return null;
  }

  const nameCounterOver = name.length >= NAME_MAX_LENGTH;

  return (
    <Box width="100%" paddingLeft="250px">
      <Box width="100%" padding="1rem">
        <AdminMenu />

        <Content>
          <Title>Gerar Link Futuro</Title>

          <Notice variant="info">
            <strong>Atenção</strong>
            Todo link futuro gerado nessa página é uma assinatura recorrente.
            Em caso de dúvida ou erro, consulte o time de TI.
          </Notice>

          <Notice variant="warning">
            <strong>Limite de antecedência</strong>
            Gere link futuro com data de primeiro pagamento de no máximo {MAX_TRIAL_DAYS} dias para frente — nada além disso.
          </Notice>

          <Field>
            <label htmlFor="name">Nome do plano</label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={handleNameChange}
              maxLength={NAME_MAX_LENGTH}
              placeholder="Ex: MBA Gestão Ágil de Projetos – 18 x R$277,00"
            />
            <small className={`counter ${nameCounterOver ? "over" : ""}`}>
              {name.length}/{NAME_MAX_LENGTH}
            </small>
          </Field>

          <Field>
            <label htmlFor="price">Valor (R$)</label>
            <input
              id="price"
              type="text"
              inputMode="numeric"
              value={price}
              onChange={handlePriceChange}
              placeholder="0,00"
            />
            <small>Digite só os números — a vírgula e os centavos são preenchidos automaticamente.</small>
          </Field>

          <Field>
            <label htmlFor="interval">Intervalo de cobrança</label>
            <select
              id="interval"
              value={interval}
              onChange={(e) => setIntervalValue(e.target.value)}
            >
              {INTERVAL_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </Field>

          <Field>
            <label>Curso</label>
            <CursosSelector noLabel handleChange={handleCursoChange} />
          </Field>

          <Field>
            <label htmlFor="cycles">Ciclos de cobrança</label>
            <input
              id="cycles"
              type="text"
              inputMode="numeric"
              value={cycles}
              onChange={handleCyclesChange}
              placeholder="Ex: 18"
            />
            <small>Inteiro positivo — sem decimais nem números negativos.</small>
          </Field>

          <Field>
            <label htmlFor="dataPrimeiroPagamento">Data do primeiro pagamento</label>
            <input
              id="dataPrimeiroPagamento"
              type="date"
              value={dataPrimeiroPagamento}
              min={dateLimits.min}
              max={dateLimits.max}
              onChange={(e) => setDataPrimeiroPagamento(e.target.value)}
            />
            <small>
              Entre {new Date(dateLimits.min + "T00:00:00").toLocaleDateString("pt-BR")} e {new Date(dateLimits.max + "T00:00:00").toLocaleDateString("pt-BR")}.
            </small>
          </Field>

          <SubmitButton onClick={handleSubmit} disabled={submitting}>
            {submitting ? "Gerando..." : "Gerar link futuro"}
          </SubmitButton>
        </Content>
      </Box>
    </Box>
  );
};

export default PlanosLinkFuturo;
